import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, X, Plus, Trash2, Eye, ArrowLeft, ListChecks, Banknote, RotateCcw } from "lucide-react";
import type { QuoteFormData, QuoteLineItem } from "@/hooks/useQuotes";

interface DevisFormProps {
  onSubmit: (data: QuoteFormData) => Promise<any>;
  onClose: () => void;
  loading?: boolean;
}

type LineItemDraft = Omit<QuoteLineItem, "id" | "quote_id">;

const DELAY_OPTIONS = [
  { value: "within_24h",    label: "Sous 24h" },
  { value: "within_3_days", label: "Sous 3 jours" },
  { value: "within_1_week", label: "Sous 1 semaine" },
  { value: "flexible",      label: "À convenir" },
];

const getMinDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return d.toISOString().split("T")[0];
};

const getMaxDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 90);
  return d.toISOString().split("T")[0];
};

const SectionTitle = ({ icon: Icon, label }: { icon: React.ElementType; label: string }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon className="h-4 w-4 text-primary shrink-0" />
    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
  </div>
);

const DevisForm = ({ onSubmit, onClose, loading }: DevisFormProps) => {
  const [step, setStep] = useState<"edit" | "preview">("edit");

  const [form, setForm] = useState({
    title: "",
    description: "",
    total_amount: "",
    valid_until: getMinDate(),
    estimated_intervention_delay: "",
  });

  const [lineItems, setLineItems] = useState<LineItemDraft[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // false = montant synchronisé avec les prestations ; true = saisi manuellement
  const [isAmountManual, setIsAmountManual] = useState(false);

  const computeLineItemsTotal = (items: LineItemDraft[]) =>
    Math.round(items.reduce((acc, item) => acc + item.total_price, 0) * 100) / 100;

  const resyncTotal = () => {
    setIsAmountManual(false);
    const sum = computeLineItemsTotal(lineItems);
    setForm((f) => ({ ...f, total_amount: sum > 0 ? String(sum) : "" }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.title.trim()) e.title = "Le titre est requis";
    else if (form.title.length > 100) e.title = "Max 100 caractères";
    if (!form.description.trim()) e.description = "La description est requise";
    else if (form.description.length > 1000) e.description = "Max 1000 caractères";
    if (!form.total_amount || Number(form.total_amount) <= 0)
      e.total_amount = "Montant invalide (doit être > 0)";
    if (!form.valid_until) e.valid_until = "Date de validité requise";
    else if (form.valid_until < getMinDate()) e.valid_until = "Date trop proche (min +3j)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validate()) setStep("preview");
  };

  const handleSend = async () => {
    const payload: QuoteFormData = {
      title: form.title.trim(),
      description: form.description.trim(),
      total_amount: Number(form.total_amount),
      valid_until: form.valid_until,
      estimated_intervention_delay: form.estimated_intervention_delay || undefined,
      line_items: lineItems.length > 0 ? lineItems : undefined,
    };
    const result = await onSubmit(payload);
    if (result) onClose();
  };

  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      { label: "", description: "", quantity: 1, unit: "service", unit_price: 0, total_price: 0 },
    ]);
  };

  const removeLineItem = (idx: number) => {
    const next = lineItems.filter((_, i) => i !== idx);
    setLineItems(next);
    if (!isAmountManual) {
      const sum = computeLineItemsTotal(next);
      setForm((f) => ({ ...f, total_amount: sum > 0 ? String(sum) : "" }));
    }
  };

  const updateLineItem = (idx: number, field: keyof LineItemDraft, value: any) => {
    const updated = [...lineItems];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === "quantity" || field === "unit_price") {
      const qty   = field === "quantity"   ? Number(value) : Number(updated[idx].quantity);
      const price = field === "unit_price" ? Number(value) : Number(updated[idx].unit_price);
      updated[idx].total_price = Math.round(qty * price * 100) / 100;
    }
    setLineItems(updated);
    if (!isAmountManual) {
      const sum = computeLineItemsTotal(updated);
      setForm((f) => ({ ...f, total_amount: sum > 0 ? String(sum) : "" }));
    }
  };

  const delayLabel = DELAY_OPTIONS.find((o) => o.value === form.estimated_intervention_delay)?.label;

  // ── PREVIEW ────────────────────────────────────────────────────────────────
  if (step === "preview") {
    return (
      <div className="border rounded-xl bg-card max-w-lg mx-auto overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-display font-semibold text-sm flex items-center gap-2">
            <Eye className="h-4 w-4 text-primary" />
            Récapitulatif du devis
          </h3>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div className="rounded-lg border bg-muted/30 p-4 space-y-4 text-sm">
            {/* Titre + description */}
            <div>
              <p className="font-semibold text-base">{form.title}</p>
              <p className="text-muted-foreground mt-1 whitespace-pre-line text-sm">{form.description}</p>
            </div>

            {/* Prestations */}
            {lineItems.length > 0 && (
              <div className="border-t pt-3 space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Prestations</p>
                {lineItems.map((item, i) => (
                  <div key={i} className="flex justify-between items-baseline text-xs">
                    <span className="text-foreground">{item.label} <span className="text-muted-foreground">× {item.quantity}</span></span>
                    <span className="font-medium tabular-nums">{item.total_price.toLocaleString("fr-DZ")} DZD</span>
                  </div>
                ))}
              </div>
            )}

            {/* Montant + validité */}
            <div className="border-t pt-3 grid grid-cols-2 gap-3">
              <div className="bg-primary/5 rounded-lg p-2.5">
                <p className="text-[10px] text-muted-foreground font-medium">Montant total</p>
                <p className="font-bold text-lg text-primary mt-0.5 tabular-nums">
                  {Number(form.total_amount).toLocaleString("fr-DZ")} DZD
                </p>
              </div>
              <div className="bg-muted/40 rounded-lg p-2.5">
                <p className="text-[10px] text-muted-foreground font-medium">Valide jusqu'au</p>
                <p className="font-semibold mt-0.5">
                  {new Date(form.valid_until).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                </p>
              </div>
            </div>

            {/* Délai */}
            {delayLabel && (
              <div className="border-t pt-3">
                <p className="text-xs text-muted-foreground">Délai d'intervention</p>
                <p className="font-medium">{delayLabel}</p>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setStep("edit")}>
              <ArrowLeft className="h-4 w-4 me-1" /> Modifier
            </Button>
            <Button type="button" variant="hero" className="flex-1" onClick={handleSend} disabled={loading}>
              {loading ? "Envoi…" : "Envoyer au client"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── FORMULAIRE ─────────────────────────────────────────────────────────────
  return (
    <div className="border rounded-xl bg-card max-w-lg mx-auto overflow-hidden">

      {/* En-tête */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-card">
        <h3 className="font-display font-semibold text-sm flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          Créer un devis
        </h3>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-4 space-y-5">

        {/* ── 1. Informations générales ────────────────────────────────────── */}
        <div>
          <SectionTitle icon={FileText} label="Informations générales" />
          <div className="space-y-3">
            <div>
              <Label className="text-xs mb-1 block">Titre du devis *</Label>
              <Input
                placeholder="Ex: Réparation fuite sous évier"
                value={form.title}
                maxLength={100}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className={errors.title ? "border-destructive" : ""}
              />
              {errors.title && <p className="text-xs text-destructive mt-1">{errors.title}</p>}
            </div>

            <div>
              <Label className="text-xs mb-1 block">Description des travaux *</Label>
              <Textarea
                placeholder="Décrivez les travaux à réaliser…"
                value={form.description}
                maxLength={1000}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={`min-h-[80px] text-sm ${errors.description ? "border-destructive" : ""}`}
              />
              {errors.description && <p className="text-xs text-destructive mt-1">{errors.description}</p>}
            </div>
          </div>
        </div>

        {/* ── 2. Prestations du devis ──────────────────────────────────────── */}
        <div className="rounded-xl border-2 bg-muted/20 p-4">
          <SectionTitle icon={ListChecks} label="Prestations du devis" />
          <p className="text-xs text-muted-foreground -mt-2 mb-3">
            Détaillez les travaux ou services inclus dans ce devis.
          </p>

          {/* Liste des lignes */}
          {lineItems.length === 0 ? (
            <div className="rounded-lg border border-dashed py-5 px-4 text-center bg-background/60 mb-3">
              <ListChecks className="h-7 w-7 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Aucune prestation ajoutée.</p>
              <p className="text-xs text-muted-foreground/70 mt-0.5">
                Ajoutez vos prestations pour rendre votre devis clair et professionnel.
              </p>
            </div>
          ) : (
            <div className="space-y-2 mb-3">
              {lineItems.map((item, idx) => (
                <div key={idx} className="border rounded-lg p-3 space-y-2 bg-background">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Prestation {idx + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => removeLineItem(idx)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Input
                    placeholder="Libellé *"
                    value={item.label}
                    className="h-8 text-xs"
                    onChange={(e) => updateLineItem(idx, "label", e.target.value)}
                  />
                  <div className="grid grid-cols-3 gap-1.5">
                    <div>
                      <span className="text-[10px] text-muted-foreground">Qté</span>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={item.quantity}
                        className="h-8 text-xs"
                        onChange={(e) => updateLineItem(idx, "quantity", e.target.value)}
                      />
                    </div>
                    <div>
                      <span className="text-[10px] text-muted-foreground">Prix unitaire</span>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={item.unit_price}
                        className="h-8 text-xs"
                        onChange={(e) => updateLineItem(idx, "unit_price", e.target.value)}
                      />
                    </div>
                    <div>
                      <span className="text-[10px] text-muted-foreground">Total</span>
                      <Input
                        type="number"
                        value={item.total_price}
                        readOnly
                        className="h-8 text-xs bg-muted/50"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full h-9 text-xs font-medium border-dashed hover:border-solid hover:bg-primary/5 hover:text-primary hover:border-primary/40 transition-colors"
            onClick={addLineItem}
          >
            <Plus className="h-3.5 w-3.5 me-1.5" /> Ajouter une prestation
          </Button>
        </div>

        {/* ── 3. Informations clés du devis ────────────────────────────────── */}
        <div className="rounded-xl border bg-card p-4">
          <SectionTitle icon={Banknote} label="Informations clés" />

          <div className="space-y-3">
            {/* Montant + validité côte à côte */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-medium">Montant total (DZD) *</Label>
                  {isAmountManual && lineItems.length > 0 && (
                    <button
                      type="button"
                      onClick={resyncTotal}
                      className="flex items-center gap-0.5 text-[10px] text-primary hover:underline"
                    >
                      <RotateCcw className="h-2.5 w-2.5" /> Recalculer
                    </button>
                  )}
                </div>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="0"
                  value={form.total_amount}
                  onChange={(e) => {
                    setIsAmountManual(true);
                    setForm({ ...form, total_amount: e.target.value });
                  }}
                  className={`font-semibold ${!isAmountManual && lineItems.length > 0 ? "bg-primary/5" : ""} ${errors.total_amount ? "border-destructive" : ""}`}
                />
                {errors.total_amount && <p className="text-xs text-destructive mt-1">{errors.total_amount}</p>}
                {!isAmountManual && lineItems.length > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1">Calculé depuis les prestations</p>
                )}
              </div>
              <div>
                <Label className="text-xs mb-1 block font-medium">Valide jusqu'au *</Label>
                <Input
                  type="date"
                  value={form.valid_until}
                  min={getMinDate()}
                  max={getMaxDate()}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  className={errors.valid_until ? "border-destructive" : ""}
                />
                {errors.valid_until && <p className="text-xs text-destructive mt-1">{errors.valid_until}</p>}
              </div>
            </div>


          </div>
        </div>

        {/* ── Actions ──────────────────────────────────────────────────────── */}
        <div className="flex gap-2 pt-1">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            Annuler
          </Button>
          <Button type="button" variant="hero" className="flex-1" onClick={handleNext}>
            <Eye className="h-4 w-4 me-1" /> Prévisualiser
          </Button>
        </div>

      </div>
    </div>
  );
};

export default DevisForm;
